import React from 'react';
import { Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import {
  formatCareSourceRecordedAt,
  getCareSourceDisplayStatus,
  getCareSourceTitle,
} from './careSourceStatusModel';
import { getMedicationSourceSuggestions } from './medicationSourceSuggestions';

export const getMedicationUploadProgress = (source = {}) => {
  const foundCount = Number(
    source.medicationReviewSummary?.foundCount
    || getMedicationSourceSuggestions(source).length
    || 0
  );
  const reviewed = getCareSourceDisplayStatus(source).label === 'Reviewed';
  return {
    decidedCount: reviewed ? foundCount : 0,
    foundCount,
    reviewed,
  };
};

const MedicationUploadedSourceCard = ({ source = {}, onReview, onViewOriginal }) => {
  const { decidedCount, foundCount, reviewed } = getMedicationUploadProgress(source);
  const status = getCareSourceDisplayStatus(source);
  const savedAt = formatCareSourceRecordedAt(source);
  const progress = foundCount ? (decidedCount / foundCount) * 100 : 0;
  const needsReview = status.label === 'Needs your review' || (!reviewed && foundCount > 0);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: colors.semantic.profileFlow.cardBg,
        overflow: 'hidden',
      }}
    >
      <Stack spacing={0.9} sx={{ p: { xs: 1.35, sm: 1.55 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.1em', color: colors.semantic.ink2 }}>
            UPLOADED SOURCE
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 850, color: colors.semantic.ink2, whiteSpace: 'nowrap' }}>
            {reviewed ? 'Reviewed' : foundCount ? `${decidedCount} of ${foundCount} decided` : status.label}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: colors.landing.heroText, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
          {getCareSourceTitle(source)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Medication review progress"
          sx={{
            height: 5,
            borderRadius: '999px',
            bgcolor: colors.semantic.profileFlow.notStartedBg,
            '& .MuiLinearProgress-bar': {
              bgcolor: needsReview ? colors.semantic.warning : colors.semantic.profileFlow.medicationManager.primaryAction,
            },
          }}
        />
        {savedAt ? (
          <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.56) }}>
            Saved {savedAt}
          </Typography>
        ) : null}
      </Stack>

      <Stack spacing={1} sx={{ p: { xs: 1.35, sm: 1.55 }, borderTop: `1px solid ${colors.semantic.profileFlow.cardBorder}` }}>
        <Typography sx={{ fontSize: '0.86rem', color: colors.semantic.ink2, lineHeight: 1.45 }}>
          {reviewed
            ? 'The original upload and medication decisions remain available for historical reference.'
            : foundCount
              ? `Nothing is added to the medication list until you decide on each of the ${foundCount} medication${foundCount === 1 ? '' : 's'} found.`
              : status.detail}
        </Typography>
        {(needsReview || reviewed) ? (
          <Button
            variant="contained"
            aria-label="Review medications"
            onClick={() => onReview?.(source)}
            sx={{
              minHeight: 48,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 900,
              boxShadow: 'none',
              bgcolor: colors.semantic.profileFlow.medicationManager.primaryAction,
              '&:hover': { bgcolor: colors.semantic.profileFlow.medicationManager.primaryActionHover, boxShadow: 'none' },
            }}
          >
            {reviewed ? 'View decisions' : `Review ${foundCount || ''} medication${foundCount === 1 ? '' : 's'}`.replace(/\s+/g, ' ').trim()}
          </Button>
        ) : null}
        <Button
          variant="text"
          onClick={() => onViewOriginal?.(source)}
          sx={{ alignSelf: 'center', textTransform: 'none', fontWeight: 850, color: colors.semantic.profileFlow.medicationManager.actionText }}
        >
          View original
        </Button>
      </Stack>
    </Paper>
  );
};

export default MedicationUploadedSourceCard;
